import { useMemo } from "react";
import { useDebounce } from "@/hooks/use-debounce";
import { translateOptional } from "@/i18n/translate";
import { useMonitoringPipeline } from "../../api";
import { useMonitoring } from "../../monitoring-context";
import type { EventFilters, EventLevel, MonitoringEvent, MonitoringWindow } from "../../types";

/**
 * Event log semantics of the backend (pizza-backend src/monitoring/events.py, service.query_events):
 * floods of the same fingerprint (source.code@subject) are folded into one stored event with `repeats`
 * and `last_ts`; with persistence off the log is the last 400 events of the answering instance (the backend
 * applies the same filters to that ring).
 */

export const EVENT_LEVELS: EventLevel[] = ["error", "warning", "info"];
export const DEFAULT_LEVELS: EventLevel[] = ["error", "warning"];

/** the sources the backend records events under (others are shown as they come) */
export const EVENT_SOURCES = [
  "http",
  "pod_pool",
  "camera",
  "thingsboard",
  "evaluation",
  "storage",
  "biteberry",
  "backend",
  "push",
] as const;

export const EVENT_LIMITS = [200, 500, 1000] as const;

/** size of the in-memory event ring of one instance */
export const MEMORY_RING_SIZE = 400;

/** an event is "ongoing" while it was seen this recently */
export const ONGOING_MS = 5 * 60_000;

/** source label in the UI language (an unknown source shows as it is) */
export const sourceLabel = (source: string) => translateOptional(`monitoring.events.source.${source}`) ?? source;

// --- time range -------------------------------------------------------------------------------------------
export type EventRange = "window" | "15m" | "1h" | "6h" | "24h" | "7d" | "30d";
export const EVENT_RANGES: EventRange[] = ["window", "15m", "1h", "6h", "24h", "7d", "30d"];

/** length and alignment step (ms) of a range; the board windows use the steps of the metric charts */
const RANGE_SPEC: Record<Exclude<EventRange, "window">, [number, number]> = {
  "15m": [15 * 60_000, 60_000],
  "1h": [3_600_000, 60_000],
  "6h": [6 * 3_600_000, 300_000],
  "24h": [86_400_000, 900_000],
  "7d": [7 * 86_400_000, 7_200_000],
  "30d": [30 * 86_400_000, 3_600_000],
};

/**
 * Start of a range, aligned like the metric buckets (the board window starts where its chart starts).
 * The value only moves when `anchor` crosses a step, so the query key stays stable between polls.
 */
export function rangeSince(range: EventRange, boardWindow: MonitoringWindow, anchor: number): number {
  const [length, step] = RANGE_SPEC[range === "window" ? boardWindow : range];
  return (Math.floor(anchor / step) + 1) * step - length;
}

// --- filters ----------------------------------------------------------------------------------------------
export interface LogFilterState {
  levels: EventLevel[];
  /** "" = every source */
  source: string;
  q: string;
  pizzaId: string;
  range: EventRange;
  limit: number;
  /** one exact group (source.code@subject), set from the grouped problems; "" = none */
  fingerprint: string;
}

export const DEFAULT_LOG_FILTERS: LogFilterState = {
  levels: DEFAULT_LEVELS,
  source: "",
  q: "",
  pizzaId: "",
  range: "window",
  limit: EVENT_LIMITS[0],
  fingerprint: "",
};

export const isDefaultFilters = (filters: LogFilterState) =>
  filters.source === DEFAULT_LOG_FILTERS.source &&
  !filters.fingerprint &&
  !filters.q.trim() &&
  !filters.pizzaId.trim() &&
  filters.range === DEFAULT_LOG_FILTERS.range &&
  filters.limit === DEFAULT_LOG_FILTERS.limit &&
  filters.levels.length === DEFAULT_LEVELS.length &&
  DEFAULT_LEVELS.every((level) => filters.levels.includes(level));

/**
 * The filters the memory log cannot apply (the backend reads the ring by level only), applied here the
 * same way the database query does: text over message / code / subject or an exact pizza id.
 */
export function filterInBrowser(events: MonitoringEvent[], filters: EventFilters): MonitoringEvent[] {
  const needle = filters.q.trim().toLowerCase();
  const pizzaId = filters.pizzaId.trim();
  return events.filter((event) => {
    if (filters.levels.length && !filters.levels.includes(event.level)) return false;
    if (filters.sources.length && !filters.sources.includes(event.source)) return false;
    if (pizzaId && event.pizza_id !== pizzaId) return false;
    if (filters.fingerprint && event.fingerprint !== filters.fingerprint) return false;
    if (filters.sinceMs && event.ts < filters.sinceMs) return false;
    if (!needle) return true;
    return (
      [event.message, event.code, event.subject ?? ""].some((value) => value.toLowerCase().includes(needle)) ||
      event.pizza_id === filters.q.trim()
    );
  });
}

/**
 * The query filters of the log: text and pizza id debounced (300 ms), the range turned into an aligned
 * start. `anchor` is the time the range ends at (moves with the board's polls, never with a timer).
 */
export function useEventQueryFilters(state: LogFilterState, anchor: number): EventFilters {
  const { window } = useMonitoring();
  const q = useDebounce(state.q, 300);
  const pizzaId = useDebounce(state.pizzaId, 300);
  const sinceMs = rangeSince(state.range, window, anchor);
  return useMemo(
    () => ({
      levels: state.levels,
      sources: state.source ? [state.source] : [],
      q,
      pizzaId,
      sinceMs,
      limit: state.limit,
      fingerprint: state.fingerprint || null,
    }),
    [state.levels, state.source, state.limit, state.fingerprint, q, pizzaId, sinceMs],
  );
}

/** branch id -> name, from the branch list the pipeline section carries */
export function useBranchNames(): Map<string, string> {
  const pipeline = useMonitoringPipeline("24h");
  const meta = pipeline.data?.branches_meta;
  return useMemo(() => new Map((meta ?? []).map((branch) => [branch.id, branch.name])), [meta]);
}
